import { useCallback, useEffect, useState } from 'react';
import { leaveRequestDetailSchema, overtimeRequestDetailSchema } from '@forecastify/shared';
import { useSession } from '../../session';
import { useT, type Translate } from '../../i18n';
import { useDecisionOptions } from './decision-options';
import type { RequestDetailData, RequestDetailHandlers } from './request-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE REQUEST BY ID, WHICHEVER KIND IT IS.                             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A row in either queue carries a uuid and no discriminator — that is the C47 decision the request
 * lists already live with — so this asks the leave endpoint first and the overtime one if that says
 * `not_found`. Two reads at worst, one for the common case, and no `kind` on the wire that every
 * list would then have to carry.
 *
 * ⚠️ **`null` id is a live hook with nothing loaded**, because an overlay that mounted its hooks
 * only while open would change how many hooks its parent runs — `task-overlay.tsx` records the same
 * rule and the same reason.
 */
export function useRequestDetail(id: string | null): {
  data: RequestDetailData | null;
  failure: 'not_found' | 'unavailable' | null;
  serverError: string | null;
  reload: () => Promise<void>;
  handlers: RequestDetailHandlers;
} {
  const session = useSession();
  const t = useT();
  const [data, setData] = useState<RequestDetailData | null>(null);
  /* ⚠️⚠️ What an overtime decision chooses from — one hook, because the row menu's dialog
     asks the same question from the same lists. `decision-options.ts` argues it. */
  const options = useDecisionOptions();
  const [failure, setFailure] = useState<'not_found' | 'unavailable' | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (id === null) {
      setData(null);
      setFailure(null);
      return;
    }

    /* ⚠️ R-57 · the address may name the request by its public identifier, which says which
       kind it is — so `lr_…` asks the leave endpoint alone and `ot_…` the overtime one alone.
       Only a uuid (an older link, an email) still needs the leave-then-overtime fallback. */
    const kind = addressKind(id);

    if (kind !== 'overtime') {
      const leave = await session.request(`/leave-requests/${id}/detail`).catch(() => null);
      if (leave?.ok === true) {
        const parsed = leaveRequestDetailSchema.safeParse(await leave.json());
        if (parsed.success) {
          setFailure(null);
          setData({ kind: 'leave', request: parsed.data });
          return;
        }
        setFailure('unavailable');
        return;
      }

      /* ⚠️ Only a `not_found` is worth a second read: a 403 or a broken connection means the same
         for both kinds, and asking twice would turn one refusal into two. An `lr_…` address that
         is not found is not an overtime claim either. */
      if (leave !== null && leave.status !== 404) {
        setFailure('unavailable');
        return;
      }
      if (kind === 'leave') {
        setFailure(leave === null ? 'unavailable' : 'not_found');
        return;
      }
    }

    const overtime = await session.request(`/overtime/${id}`).catch(() => null);
    if (overtime?.ok !== true) {
      setFailure(overtime?.status === 404 ? 'not_found' : 'unavailable');
      return;
    }
    const parsed = overtimeRequestDetailSchema.safeParse(await overtime.json());
    if (!parsed.success) {
      setFailure('unavailable');
      return;
    }
    setFailure(null);
    setData({ kind: 'overtime', claim: parsed.data });
  }, [session, id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* ⚠️ R-57 · the address may be an identifier, but every write names the row by its uuid — the
     batch endpoints take uuids — so the handlers read it off what loaded, never off the address. */
  const uuid = data === null ? null : data.kind === 'leave' ? data.request.id : data.claim.id;

  const write = async (path: string, body: unknown): Promise<void> => {
    setServerError(null);
    const response = await session
      .request(path, { method: 'POST', body: JSON.stringify(body) })
      .catch(() => null);
    if (response?.ok !== true) {
      const envelope = (await response?.json().catch(() => null)) as
        { error?: { code?: string } } | null | undefined;
      setServerError(messageFor(t, envelope?.error?.code ?? 'unavailable'));
      return;
    }
    await reload();
  };

  const handlers: RequestDetailHandlers = {
    options,
    onDecide: (decision, note, settle) => {
      void (async () => {
        if (uuid === null) return;
        setServerError(null);
        /**
         * ⚠️⚠️ **THE BATCH ENDPOINT WITH ONE ID, AND ITS REFUSALS ARE IN THE BODY.** A single
         * decision that came back `{ decided: [], refused: [...] }` is an HTTP 200 that changed
         * nothing — `attachment_required` is the one somebody will actually hit.
         */
        const path = data?.kind === 'overtime' ? '/overtime/decide' : '/leave-requests/decide';
        const response = await session
          .request(path, {
            method: 'POST',
            body: JSON.stringify({
              ids: [uuid],
              decision,
              note: note === '' ? null : note,
              /* ⚠️ Item 31, and only on an overtime claim — the leave endpoint ignores it. */
              ...(settle ?? {}),
            }),
          })
          .catch(() => null);
        if (response?.ok !== true) {
          setServerError(t('error.unavailable'));
          return;
        }
        const body = (await response.json().catch(() => null)) as {
          refused?: { code?: string }[];
        } | null;
        const refusal = body?.refused?.[0]?.code;
        if (refusal !== undefined) setServerError(messageFor(t, refusal));
        await reload();
      })();
    },
    onRevoke: () => {
      if (uuid === null || data === null) return;
      if (data.kind === 'overtime') {
        void write(`/overtime/${uuid}/revoke`, { version: data.claim.version });
        return;
      }
      void write(`/leave-requests/${uuid}/revoke`, { version: data.request.version });
    },
    onComment: (bodyHtml) => {
      if (uuid === null) return;
      void write(`/leave-requests/${uuid}/comments`, { bodyHtml });
    },
    onAttach: async (file) => {
      if (uuid === null) return 'unavailable';
      const form = new FormData();
      form.set('file', file);
      const response = await session
        .request(`/files?target=leave_request&targetId=${uuid}`, { method: 'POST', body: form })
        .catch(() => null);
      if (response?.ok === true) {
        await reload();
        return null;
      }
      const envelope = (await response?.json().catch(() => null)) as
        { error?: { code?: string } } | null | undefined;
      return envelope?.error?.code ?? 'unavailable';
    },
  };

  return { data, failure, serverError, reload, handlers };
}

/** ⚠️ One `t()` per branch with a literal key — the refusals this screen can produce. */
function messageFor(t: Translate, code: string): string {
  if (code === 'attachment_required') return t('leave.error.attachment_required');
  if (code === 'overlaps') return t('leave.error.overlaps');
  /* 30 Oct 2026 · a rejected or already revoked request — nothing left to withdraw. */
  if (code === 'not_revocable') return t('leave.error.not_revocable');
  /* 4 Nov 2026 · approved leave that has already begun. */
  if (code === 'already_started') return t('leave.error.already_started');
  if (code === 'not_pending') return t('error.conflict');
  /* ⚠️ Its own sentence, not `permission_denied`: no role fixes it, so saying *"you may not"*
     sends somebody looking for a setting that cannot exist. */
  if (code === 'cannot_decide_own') return t('leave.error.cannot_decide_own');
  if (code === 'permission_denied') return t('error.permission_denied');
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  return t('error.unavailable');
}

/**
 * Which kind an address names, when it says so — R-57's public identifiers carry the kind in their
 * prefix (`lr_…`, `ot_…`); a uuid does not, and is `null`.
 */
function addressKind(id: string): 'leave' | 'overtime' | null {
  if (id.startsWith('lr_')) return 'leave';
  if (id.startsWith('ot_')) return 'overtime';
  return null;
}
